import { CATALOG_PATHS, type RawCatalogs } from '../catalog/types'
import { parseQuest, toBase64 } from '../domain/serialize'
import type { Quest } from '../domain/types'
import { API_ROOT, DEFAULT_BRANCH, REPO_NAME, REPO_OWNER } from './config'

export class GitHubError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly isConflict = false,
  ) {
    super(message)
    this.name = 'GitHubError'
  }
}

function headers(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    Accept: 'application/vnd.github+json',
    'X-GitHub-Api-Version': '2022-11-28',
    'Content-Type': 'application/json',
  }
}

async function fail(response: Response): Promise<never> {
  let detail = response.statusText
  try {
    const body = (await response.json()) as { message?: string }
    if (body.message) detail = body.message
  } catch {
    /* keep statusText */
  }
  if (response.status === 401)
    throw new GitHubError('Token rejected. It may have expired or been revoked.', 401)
  if (response.status === 403)
    throw new GitHubError(`Forbidden — check the token grants Contents: read & write. (${detail})`, 403)
  if (response.status === 404)
    throw new GitHubError(`Not found — check the token is scoped to ${REPO_OWNER}/${REPO_NAME}. (${detail})`, 404)
  if (response.status === 409 || response.status === 422)
    throw new GitHubError(detail, response.status, true)
  throw new GitHubError(detail, response.status)
}

/** One file as read from the repo, with the blob SHA needed to write it back safely. */
export interface LoadedFile {
  path: string
  text: string
  sha: string
}

export interface Snapshot {
  commitSha: string
  quests: Map<string, { quest: Quest; sha: string }>
  /** Files that failed to parse — surfaced rather than silently dropped. */
  broken: { path: string; error: string }[]
  catalogs: RawCatalogs
}

/**
 * Read the entire dataset in a single GraphQL request.
 *
 * The REST alternative is one round-trip per file — 65 of them. GraphQL aliases let
 * one query pull every quest and catalog at once (~190KB, ~2s) and return each
 * blob's oid, which is exactly the SHA the Contents API needs for conflict-safe writes.
 */
export async function loadSnapshot(token: string): Promise<Snapshot> {
  const listing = await listQuestPaths(token)
  const paths = [...listing.paths, ...CATALOG_PATHS]

  const aliases = paths.map((path, index) => `f${index}: object(expression: "${listing.commitSha}:${path}") { ... on Blob { oid text } }`)
  const query = `query { repository(owner: "${REPO_OWNER}", name: "${REPO_NAME}") { ${aliases.join(' ')} } }`

  const response = await fetch(`${API_ROOT}/graphql`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify({ query }),
  })
  if (!response.ok) await fail(response)

  const body = (await response.json()) as {
    data?: { repository?: Record<string, { oid: string; text: string } | null> }
    errors?: { message: string }[]
  }
  if (body.errors?.length) throw new GitHubError(body.errors.map((e) => e.message).join('; '), 500)
  const repository = body.data?.repository
  if (!repository) throw new GitHubError('Repository returned no data.', 500)

  const files = new Map<string, LoadedFile>()
  paths.forEach((path, index) => {
    const blob = repository[`f${index}`]
    if (blob) files.set(path, { path, text: blob.text, sha: blob.oid })
  })

  const quests = new Map<string, { quest: Quest; sha: string }>()
  const broken: { path: string; error: string }[] = []
  for (const path of listing.paths) {
    const file = files.get(path)
    if (!file) {
      broken.push({ path, error: 'File could not be read.' })
      continue
    }
    try {
      const quest = parseQuest(file.text)
      quests.set(quest.id, { quest, sha: file.sha })
    } catch (error) {
      broken.push({ path, error: error instanceof Error ? error.message : String(error) })
    }
  }

  const catalogFor = <T>(path: string): T => {
    const file = files.get(path)
    if (!file) throw new GitHubError(`Missing ${path}. The repo may be mid-export.`, 500)
    return JSON.parse(file.text) as T
  }

  return {
    commitSha: listing.commitSha,
    quests,
    broken,
    catalogs: {
      items: catalogFor('catalog/items.json'),
      tools: catalogFor('catalog/tools.json'),
      locations: catalogFor('catalog/locations.json'),
      estates: catalogFor('catalog/estates.json'),
      boosts: catalogFor('catalog/boosts.json'),
      skins: catalogFor('catalog/skins.json'),
    },
  }
}

/** The tree endpoint gives every path plus the head commit in one request. */
async function listQuestPaths(token: string): Promise<{ commitSha: string; paths: string[] }> {
  const commitSha = await headCommit(token)
  const response = await fetch(
    `${API_ROOT}/repos/${REPO_OWNER}/${REPO_NAME}/git/trees/${commitSha}?recursive=1`,
    { headers: headers(token) },
  )
  if (!response.ok) await fail(response)
  const body = (await response.json()) as { tree: { path: string; type: string }[] }
  const paths = body.tree
    .filter((entry) => entry.type === 'blob' && entry.path.startsWith('quests/') && entry.path.endsWith('.json'))
    .map((entry) => entry.path)
    .sort()
  return { commitSha, paths }
}

export async function headCommit(token: string): Promise<string> {
  const response = await fetch(
    `${API_ROOT}/repos/${REPO_OWNER}/${REPO_NAME}/commits/${DEFAULT_BRANCH}`,
    { headers: { ...headers(token), Accept: 'application/vnd.github.sha' } },
  )
  if (!response.ok) await fail(response)
  return (await response.text()).trim()
}

export interface WriteResult {
  commitSha: string
  /** New blob SHA — must replace the cached one or the next write conflicts. */
  sha: string
}

/**
 * Write one quest file.
 *
 * Passing the blob SHA we read gives optimistic locking for free: if the file
 * changed on GitHub since load — the other editor saved, or Unity re-exported —
 * GitHub rejects the write rather than silently clobbering it.
 */
export async function putQuestFile(
  token: string,
  path: string,
  content: string,
  message: string,
  sha: string | null,
  branch: string = DEFAULT_BRANCH,
): Promise<WriteResult> {
  const response = await fetch(`${API_ROOT}/repos/${REPO_OWNER}/${REPO_NAME}/contents/${path}`, {
    method: 'PUT',
    headers: headers(token),
    body: JSON.stringify({
      message,
      content: toBase64(content),
      branch,
      ...(sha ? { sha } : {}),
    }),
  })
  if (!response.ok) await fail(response)
  const body = (await response.json()) as {
    content: { sha: string }
    commit: { sha: string }
  }
  return { commitSha: body.commit.sha, sha: body.content.sha }
}

export async function whoAmI(token: string): Promise<{ login: string }> {
  const response = await fetch(`${API_ROOT}/user`, { headers: headers(token) })
  if (!response.ok) await fail(response)
  return (await response.json()) as { login: string }
}
